/**
 * The persistent strip.
 *
 * One job above all others: make the gap between private support and public participation
 * impossible to miss. It is drawn as a single bar where the filled red portion is what
 * people are actually doing, the blue rule marks how far support extends, and the space
 * between them is left deliberately empty — a void rather than a third colour, because
 * the quantity being shown is an absence. It is the people who want this to work and are
 * not acting on it.
 *
 * Everything else on the strip is secondary and typeset accordingly.
 */

import type { DaySnapshot, RunLog } from 'walkout-engine';
import './TopStrip.css';

function pct(value: number): string {
  const percent = value * 100;
  if (percent >= 10) return `${Math.round(percent)}`;
  if (percent >= 1) return percent.toFixed(1);
  return percent.toFixed(2);
}

export function TopStrip({ run, day }: { run: RunLog; day: DaySnapshot }) {
  const standing = day.pillars.filter((p) => p.defectedOn === null).length;
  const defected = day.pillars.length - standing;

  return (
    <header className="strip">
      <div className="strip__day">
        <span className="label">Day</span>
        <span className="strip__day-value numeral">{day.day}</span>
        <span className="strip__day-of">of {run.config.horizonDays}</span>
      </div>

      <div className="strip__gap">
        <div className="strip__gap-head">
          <span className="strip__gap-figure numeral" style={{ color: 'var(--c-participation)' }}>
            {pct(day.participation)}%
          </span>
          <span className="strip__gap-label label">out</span>

          <span className="strip__gap-void numeral">{pct(day.gap)}%</span>
          <span className="strip__gap-label strip__gap-label--void label">
            want it and are not acting
          </span>

          <span className="strip__gap-figure numeral" style={{ color: 'var(--c-support)' }}>
            {pct(day.privateSupport)}%
          </span>
          <span className="strip__gap-label label">privately support</span>
        </div>

        <div
          className="strip__bar"
          role="img"
          aria-label={`${pct(day.participation)}% of the workforce is out. ${pct(day.privateSupport)}% privately supports the demands. The gap is ${pct(day.gap)}%.`}
        >
          <div className="strip__bar-track" />
          <div
            className="strip__bar-support"
            style={{ width: `${Math.max(0, Math.min(100, day.privateSupport * 100))}%` }}
          />
          <div
            className="strip__bar-out"
            style={{ width: `${Math.max(0, Math.min(100, day.participation * 100))}%` }}
          />
        </div>
      </div>

      <dl className="strip__stats">
        <div className="strip__stat">
          <dt className="label">Output lost</dt>
          <dd className="numeral">{pct(day.economy.outputLoss)}%</dd>
        </div>
        <div className="strip__stat">
          <dt className="label">Days of output</dt>
          <dd className="numeral">{day.economy.cumulativeLoss.toFixed(1)}</dd>
        </div>
        <div className="strip__stat">
          <dt className="label">Pillars standing</dt>
          <dd className={`numeral${defected > 0 ? ' strip__stat--falling' : ''}`}>
            {standing}/{day.pillars.length}
          </dd>
        </div>
        <div className="strip__stat">
          <dt className="label">Sectors out</dt>
          <dd className="numeral">{day.sectorsMobilised}</dd>
        </div>
      </dl>
    </header>
  );
}
